import type { ProjectDownload } from './common';

/**
 * Which pill a download history row shows. `no-data` is a succeeded export that wrote no file
 * because the selected layers held no data — its row stays in the history so every requested
 * format is accounted for. `null` means an unrecognised state and renders nothing.
 *
 * Kept free of runtime imports so it can be tested without loading the page's component tree.
 */
export type DownloadStatus = 'pending' | 'download' | 'no-data' | 'failed' | 'delayed' | null;

export function getDownloadStatus(
  download: Pick<ProjectDownload, 'jobStatus' | 'hasFile' | 'submissionId' | 'escalationStatus'>,
): DownloadStatus {
  const { jobStatus, hasFile, submissionId, escalationStatus } = download;

  // A restricted row has no real jobStatus yet — its escalation is the only signal available.
  if (escalationStatus === 'pending') return 'delayed';
  // Archived before it ever ran never produces a jobStatus of its own.
  if (escalationStatus === 'archived' && !jobStatus) return 'failed';

  if (jobStatus === 'esriJobExecuting' || jobStatus === 'esriJobSubmitted' || !jobStatus) return 'pending';
  // Only an export submission distinguishes "succeeded with no file" as no-data. The legacy ESRI
  // downloader marks a row succeeded as soon as its job finishes and writes the path afterwards, so
  // outside a submission that combination means the file is still being fetched, not that it's empty.
  if (jobStatus === 'esriJobSucceeded') return hasFile ? 'download' : submissionId ? 'no-data' : null;
  if (jobStatus === 'esriJobFailed' && !hasFile) return 'failed';

  return null;
}
